import React from 'react';
import { BarChart2, TrendingUp, ChevronUp, ChevronDown, Activity } from 'lucide-react';

/**
 * Компонент для отображения статистических данных о сгенерированных числах
 */
const StatisticsDisplay = ({ statistics, formData }) => {
  if (!statistics) return null;

  // Вычисление процента для каждого значения распределения (для гистограммы)
  const maxDistribution = Math.max(...statistics.distribution);
  const distributionPercentages = statistics.distribution.map(
    value => (value / maxDistribution) * 100
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center mb-4">
        <BarChart2 className="w-5 h-5 mr-2 text-indigo-500" />
        Статистика распределения
      </h3>

      {/* Основные статистические показатели */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-6">
        <StatCard 
          title="Среднее значение" 
          value={statistics.mean.toFixed(4)} 
          icon={<Activity className="w-4 h-4 text-indigo-500" />} 
        />
        
        <StatCard 
          title="Медиана" 
          value={statistics.median.toFixed(4)} 
          icon={<TrendingUp className="w-4 h-4 text-emerald-500" />} 
        />
        
        <StatCard 
          title="Мода" 
          value={typeof statistics.mode === 'number' ? statistics.mode.toFixed(4) : statistics.mode} 
          icon={<TrendingUp className="w-4 h-4 text-amber-500" />} 
        />
        
        <StatCard 
          title="Стд. отклонение" 
          value={statistics.stdDev.toFixed(4)} 
          icon={<Activity className="w-4 h-4 text-rose-500" />} 
        />
        
        <StatCard 
          title="Минимум" 
          value={statistics.min.toFixed(parseInt(formData.decimalPlaces) || 0)} 
          icon={<ChevronDown className="w-4 h-4 text-blue-500" />} 
        />
        
        <StatCard 
          title="Максимум" 
          value={statistics.max.toFixed(parseInt(formData.decimalPlaces) || 0)} 
          icon={<ChevronUp className="w-4 h-4 text-red-500" />} 
        />
        
        <StatCard 
          title="Разброс" 
          value={statistics.range.toFixed(4)} 
          icon={<Activity className="w-4 h-4 text-purple-500" />} 
        />
        
        <StatCard 
          title="Количество" 
          value={statistics.count} 
          icon={<BarChart2 className="w-4 h-4 text-gray-500" />} 
        />
      </div>

      {/* Гистограмма распределения */}
      <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4 mt-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Распределение частот</h4>
        
        <div className="relative h-[200px] flex items-end gap-1">
          {statistics.distribution.map((value, index) => (
            <div 
              key={`bar-${index}`} 
              className="flex-1 flex flex-col items-center group"
            >
              {/* Всплывающая подсказка для каждого столбца */}
              <div className="absolute bottom-full mb-1 opacity-0 group-hover:opacity-100 bg-gray-800 dark:bg-gray-700 text-white px-2 py-1 rounded text-xs transition-opacity pointer-events-none whitespace-nowrap">
                {statistics.distributionLabels[index].min.toFixed(2)} - {statistics.distributionLabels[index].max.toFixed(2)}:&nbsp;
                {value} значений ({(value / statistics.count * 100).toFixed(1)}%)
              </div>
              
              {/* Столбец гистограммы */}
              <div 
                className="w-full bg-indigo-500 dark:bg-indigo-600 rounded-t transition-all hover:bg-indigo-600 dark:hover:bg-indigo-500"
                style={{ 
                  height: `${Math.max(distributionPercentages[index], 3)}%`,
                  opacity: 0.6 + (distributionPercentages[index] / 300)
                }}
              ></div>
              
              {/* Подпись для крупных экранов */}
              {statistics.distribution.length <= 20 && (
                <div className="text-[0.6rem] text-gray-500 dark:text-gray-400 mt-1 hidden sm:block">
                  {statistics.distributionLabels[index].min.toFixed(1)}
                </div>
              )}
            </div>
          ))}
          
          {/* Горизонтальные линии для лучшей читаемости */}
          <div className="absolute w-full h-full flex flex-col justify-between pointer-events-none">
            <div className="border-t border-gray-200 dark:border-gray-600/20 w-full"></div>
            <div className="border-t border-gray-200 dark:border-gray-600/20 w-full"></div>
            <div className="border-t border-gray-200 dark:border-gray-600/20 w-full"></div>
            <div className="border-t border-gray-200 dark:border-gray-600/20 w-full"></div>
          </div>
        </div>
        
        {/* Подписи осей */}
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-2">
          <div>
            {formData.min}
          </div>
          <div>
            {formData.max}
          </div>
        </div>
      </div>

      {/* Дополнительная информация */}
      <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
        <p className="mb-2">
          <span className="font-medium">Интерпретация:</span> 
          {' '}
          {statistics.stdDev < (statistics.max - statistics.min) / 10 
            ? 'Большинство чисел сгруппированы близко к среднему значению.'
            : statistics.stdDev < (statistics.max - statistics.min) / 4
              ? 'Числа имеют умеренное распределение вокруг среднего значения.'
              : 'Числа широко разбросаны по всему диапазону.'}
        </p>
        
        {formData.distribution === 'uniform' && (
          <p>
            Для идеального равномерного распределения ожидается близкое количество значений в каждом интервале.
          </p>
        )}
        
        {formData.distribution === 'normal' && (
          <p>
            Для нормального распределения характерна "колоколообразная" форма гистограммы с наибольшим количеством значений около среднего.
          </p>
        )}
      </div>
    </div>
  );
};

// Вспомогательный компонент для карточки со статистическим показателем
const StatCard = ({ title, value, icon }) => (
  <div className="p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700">
    <div className="flex items-center mb-1">
      {icon}
      <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 ml-1">
        {title}
      </h4>
    </div>
    <div className="text-lg font-semibold text-gray-900 dark:text-white">
      {value}
    </div>
  </div>
);

export default StatisticsDisplay;